<template>
  <ContentWrap>
    <!-- 搜索工作栏 -->
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="68px"
    >
      <el-form-item label="回款编号" prop="no">
        <el-input
          v-model="queryParams.no"
          class="!w-240px"
          clearable
          placeholder="请输入回款编号"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="客户名称" prop="customerId">
        <el-select
          v-model="queryParams.customerId"
          class="!w-240px"
          placeholder="请选择客户"
          @keyup.enter="handleQuery"
        >
          <el-option
            v-for="item in customerList"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
        <el-button
          v-hasPermi="['crm:receivable:create']"
          plain
          type="primary"
          @click="openForm('create')"
        >
          <Icon class="mr-5px" icon="ep:plus" />
          新增
        </el-button>
        <el-button
          v-hasPermi="['crm:receivable:export']"
          :loading="exportLoading"
          plain
          type="success"
          @click="handleExport"
        >
          <Icon class="mr-5px" icon="ep:download" />
          导出
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <!-- 列表 -->
  <ContentWrap>
    <el-tabs v-model="activeName" @tab-click="handleTabClick">
      <el-tab-pane label="我负责的" name="1" />
      <el-tab-pane label="我参与的" name="2" />
      <el-tab-pane label="下属负责的" name="3" />
    </el-tabs>
    <el-table v-loading="loading" :data="list" :show-overflow-tooltip="true" :stripe="true">
      <!-- ============ 原型列段：严格对齐 原型图/客户/财务/我的回款-列表页.png ============ -->
      <!-- 订单编号：原型为蓝色链接，但订单↔合同目前没有数据级关联（crm_contract.order_no
           只是文本冗余，拿不到 orderId），因此这里只做展示、不可跳转。 -->
      <el-table-column
        align="center"
        fixed="left"
        label="订单编号"
        prop="contract.orderNo"
        width="130"
      />
      <el-table-column align="center" label="合同编号" prop="contractNo" width="180">
        <template #default="scope">
          <el-link
            :underline="false"
            type="primary"
            @click="openContractDetail(scope.row.contractId)"
          >
            {{ scope.row.contract?.no }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column align="center" label="客户名称" prop="customerName" width="180">
        <template #default="scope">
          <el-link
            :underline="false"
            type="primary"
            @click="openCustomerDetail(scope.row.customerId)"
          >
            {{ scope.row.customerName }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column align="center" label="合同类型" width="120">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.CRM_CONTRACT_TYPE" :value="scope.row.contract?.contractType" />
        </template>
      </el-table-column>
      <el-table-column
        :formatter="erpPriceTableColumnFormatter"
        align="center"
        label="合同金额(元)"
        prop="contract.totalPrice"
        width="140"
      />
      <el-table-column align="center" label="收/付款" width="110">
        <template #default="scope">
          <dict-tag
            :type="DICT_TYPE.CRM_CONTRACT_PAYMENT_DIRECTION"
            :value="scope.row.contract?.paymentDirection"
          />
        </template>
      </el-table-column>
      <el-table-column align="center" label="付款方式" width="110">
        <template #default="scope">
          <dict-tag
            :type="DICT_TYPE.CRM_CONTRACT_PAYMENT_METHOD"
            :value="scope.row.contract?.paymentMethod"
          />
        </template>
      </el-table-column>
      <el-table-column align="center" label="付款分期" width="110">
        <template #default="scope">
          <dict-tag
            :type="DICT_TYPE.CRM_CONTRACT_INSTALLMENT"
            :value="scope.row.contract?.installment"
          />
        </template>
      </el-table-column>
      <!-- 是否收款,付款：★ 回款自身字段（不是合同属性），字典 crm_receivable_received_flag（1 是 / 0 否）。
           原型列序：付款方式 → 付款分期 → 是否收款,付款 → 说明。 -->
      <el-table-column align="center" label="是否收款,付款" width="120">
        <template #default="scope">
          <dict-tag
            :type="DICT_TYPE.CRM_RECEIVABLE_RECEIVED_FLAG"
            :value="scope.row.receivedFlag"
          />
        </template>
      </el-table-column>
      <!-- 说明：取「合同」的说明字段；与下面追加的「备注」（回款自身的 remark）是两个字段 -->
      <el-table-column align="center" label="说明" prop="contract.description" width="200" />
      <el-table-column align="center" label="创建人" prop="creatorName" width="120" />

      <!-- ====== 以下为回款自身的字段：原型列表里没有，按需求原样追加在后，避免丢信息 ====== -->
      <el-table-column align="center" label="回款编号" prop="no" width="180">
        <template #default="scope">
          <el-link :underline="false" type="primary" @click="openDetail(scope.row.id)">
            {{ scope.row.no }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column
        :formatter="dateFormatter2"
        align="center"
        label="回款日期"
        prop="returnTime"
        width="150px"
      />
      <el-table-column
        align="center"
        label="回款金额(元)"
        prop="price"
        width="140"
        :formatter="erpPriceTableColumnFormatter"
      />
      <el-table-column align="center" label="回款方式" prop="returnType" width="130px">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.CRM_RECEIVABLE_RETURN_TYPE" :value="scope.row.returnType" />
        </template>
      </el-table-column>
      <el-table-column align="center" label="备注" prop="remark" width="200" />
      <!-- 合同金额已挪到上面的「原型列段」（label 按原型改为「合同金额(元)」），此处不再重复 -->
      <el-table-column align="center" label="负责人" prop="ownerUserName" width="120" />
      <el-table-column align="center" label="所属部门" prop="ownerUserDeptName" width="100px" />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="更新时间"
        prop="updateTime"
        width="180px"
      />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="创建时间"
        prop="createTime"
        width="180px"
      />
      <!-- 创建人已挪到上面的「原型列段」，此处不再重复 -->
      <!-- ★ 「回款状态」列（即审批状态 auditStatus）按需求隐藏（2026-09-21）：审批入口已下线，
           该列恒为「未提交」。恢复：取消注释即可。 -->
      <!-- <el-table-column align="center" fixed="right" label="回款状态" prop="auditStatus" width="120">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.CRM_AUDIT_STATUS" :value="scope.row.auditStatus" />
        </template>
      </el-table-column> -->
      <el-table-column align="center" fixed="right" label="操作" width="180px">
        <template #default="scope">
          <el-button
            v-hasPermi="['crm:receivable:update']"
            link
            type="primary"
            @click="openForm('update', scope.row.id)"
          >
            编辑
          </el-button>
          <!-- ★ 「提交审核 / 查看审批」按需求隐藏（2026-09-21）：回款不走审批流，审批入口整体下线。
               （后端 BPM 流程定义 crm-receivable-audit 本就不存在，点提交必然失败。）
               恢复：把下面两段注释都取消即可；两段原本是 v-if / v-else 互斥对，
               请给「查看审批」那段补回 v-else。handleSubmit / handleProcessDetail
               两个方法都保留未删，恢复后可直接使用。 -->
          <!-- <el-button
            v-if="scope.row.auditStatus === 0"
            v-hasPermi="['crm:receivable:update']"
            link
            type="primary"
            @click="handleSubmit(scope.row)"
          >
            提交审核
          </el-button> -->
          <!-- <el-button
            v-if="scope.row.auditStatus !== 0"
            v-hasPermi="['crm:receivable:update']"
            link
            type="primary"
            @click="handleProcessDetail(scope.row)"
          >
            查看审批
          </el-button> -->
          <el-button
            v-hasPermi="['crm:receivable:delete']"
            link
            type="danger"
            @click="handleDelete(scope.row.id)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <!-- 分页 -->
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 表单弹窗：添加/修改 -->
  <ReceivableForm ref="formRef" @success="getList" />
</template>
<script lang="ts" setup>
import { DICT_TYPE } from '@/utils/dict'
import { dateFormatter, dateFormatter2 } from '@/utils/formatTime'
import download from '@/utils/download'
import * as ReceivableApi from '@/api/crm/receivable'
import ReceivableForm from './ReceivableForm.vue'
import * as CustomerApi from '@/api/crm/customer'
import { TabsPaneContext } from 'element-plus'
import { erpPriceTableColumnFormatter } from '@/utils'

// ⚠️ 必须与 system_menu.component_name（2422 → CrmReceivable）一致，否则动态路由的 name
//    与组件 name 不匹配，keep-alive 缓存失效（本次改造一并修正，原为 'Receivable'）
defineOptions({ name: 'CrmReceivable' })

const message = useMessage() // 消息弹窗
const { t } = useI18n() // 国际化
const loading = ref(true) // 列表的加载中
const total = ref(0) // 列表的总页数
const list = ref([]) // 列表的数据
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  sceneType: '1', // 默认和 activeName 相等
  no: undefined,
  customerId: undefined
})
const queryFormRef = ref() // 搜索的表单
const exportLoading = ref(false) // 导出的加载中
const activeName = ref('1') // 列表 tab
const customerList = ref<CustomerApi.CustomerVO[]>([]) // 客户列表

/** tab 切换 */
const handleTabClick = (tab: TabsPaneContext) => {
  if (tab.paneName === undefined) {
    return
  }
  queryParams.sceneType = String(tab.paneName)
  handleQuery()
}

/** 查询列表 */
const getList = async () => {
  loading.value = true
  try {
    const data = await ReceivableApi.getReceivablePage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

/** 搜索按钮操作 */
const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

/** 重置按钮操作 */
const resetQuery = () => {
  queryFormRef.value.resetFields()
  handleQuery()
}

/** 添加/修改操作 */
const formRef = ref()
const openForm = (type: string, id?: number) => {
  formRef.value.open(type, id)
}

/** 删除按钮操作 */
const handleDelete = async (id: number) => {
  try {
    // 删除的二次确认
    await message.delConfirm()
    // 发起删除
    await ReceivableApi.deleteReceivable(id)
    message.success(t('common.delSuccess'))
    // 刷新列表
    await getList()
  } catch {}
}

/** 提交审核 **/
const handleSubmit = async (row: ReceivableApi.ReceivableVO) => {
  await message.confirm(`您确定提交编号为【${row.no}】的回款审核吗？`)
  await ReceivableApi.submitReceivable(row.id)
  message.success('提交审核成功！')
  await getList()
}

/** 查看审批 */
const handleProcessDetail = (row: ReceivableApi.ReceivableVO) => {
  push({ name: 'BpmProcessInstanceDetail', query: { id: row.processInstanceId } })
}

/** 打开回款详情 */
const { push } = useRouter()
const openDetail = (id: number) => {
  push({ name: 'CrmReceivableDetail', params: { id } })
}

/** 打开客户详情 */
const openCustomerDetail = (id: number) => {
  push({ name: 'CrmCustomerDetail', params: { id } })
}

/** 打开合同详情 */
const openContractDetail = (id: number) => {
  push({ name: 'CrmContractDetail', params: { id } })
}

/** 导出按钮操作 */
const handleExport = async () => {
  try {
    // 导出的二次确认
    await message.exportConfirm()
    // 发起导出
    exportLoading.value = true
    const data = await ReceivableApi.exportReceivable(queryParams)
    download.excel(data, '回款.xls')
  } catch {
  } finally {
    exportLoading.value = false
  }
}

/** 初始化 **/
onMounted(async () => {
  await getList()
  // 获得客户列表
  customerList.value = await CustomerApi.getCustomerSimpleList()
})
</script>
